// Lesson 4: Kata Ganti & Kalimat Dasar - Question Configurations
export interface QuestionConfig {
    type: string;
    wordBank: number[]; // Word IDs to use
    questionSentence?: number[]; // Order of word IDs for sentence questions
    correctAnswer?: number[] | number; // Correct answer(s)
    options?: Array<{ id: number; text?: string }>; // Multiple choice options
    imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// Vocabulary introduction configurations
export const unit1Lesson4VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 101, // Au (Saya)
        imagePrompt: "saya"
    },
    {
        type: "vocab-intro",
        introWordId: 102, // Ho (Kamu)
        imagePrompt: "kamu"
    },
    {
        type: "vocab-intro",
        introWordId: 103, // Ia (Dia)
        imagePrompt: "dia"
    },
    {
        type: "vocab-intro",
        introWordId: 104, // Hami (Kami)
        imagePrompt: "kami"
    },
    {
        type: "vocab-intro",
        introWordId: 105, // Hamu (Kalian)
        imagePrompt: "kalian"
    }
];

// Fill in the blank configurations
export const unit1Lesson4FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [999, 112, 109, 102],
        questionSentence: [999, 112, 109, 102], // "______ do anakkon ho?" -> Aha (What is your name?)
        correctAnswer: 108, // Aha
        options: [
            { id: 108, text: "Apa" }, // Aha
            { id: 101, text: "Saya" }, // Au
            { id: 102, text: "Kamu" }, // Ho
            { id: 103, text: "Dia" }  // Ia
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [102, 112, 999],
        questionSentence: [102, 112, 999], // "Ho do _____?" -> parguron (Are you a student?)
        correctAnswer: 110, // Parguron
        options: [
            { id: 110, text: "Pelajar" }, // Parguron - correct
            { id: 101, text: "Saya" }, // Au - grammatically wrong
            { id: 102, text: "Kamu" }, // Ho - grammatically wrong
            { id: 104, text: "Kami" }  // Hami - grammatically wrong
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [999, 112, 111],
        questionSentence: [999, 112, 111], // "_____ do guru" -> Ia (He/She is a teacher)
        correctAnswer: 103, // Ia
        options: [
            { id: 103, text: "Dia" }, // Ia - correct
            { id: 110, text: "Pelajar" }, // Parguron - wrong word type
            { id: 111, text: "Guru" }, // Guru - wrong word type  
            { id: 112, text: "Adalah" }  // do - wrong word type
        ]
    }
];

// Compose sentence configurations
export const unit1Lesson4ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [102, 112, 110],
        questionSentence: [102, 112, 110], // "Ho do parguron?" - Are you a student?
        correctAnswer: [102, 112, 110]
    },
    {
        type: "compose-sentences",
        wordBank: [103, 112, 111],
        questionSentence: [103, 112, 111], // "Ia do guru." - He/She is a teacher.
        correctAnswer: [103, 112, 111]
    },
    {
        type: "compose-sentences",
        wordBank: [104, 106, 113, 107],
        questionSentence: [104, 113, 106, 107], // "Hami do martonun Tarutung" - We are from Tarutung
        correctAnswer: [104, 106, 113, 107]
    },
    {
        type: "compose-sentences",
        wordBank: [101, 112, 110],
        questionSentence: [101, 112, 110], // "Au do parguron" - I am a student
        correctAnswer: [101, 112, 110]
    }
];

// Word translation configurations
export const unit1Lesson4WordTranslationConfigs = [
    {
        type: "word-translation",
        wordBank: [104],
        correctAnswer: 104, // Hami -> Kami
        options: [
            { id: 104, text: "Kami" },
            { id: 105, text: "Kalian" },
            { id: 102, text: "Kamu" },
            { id: 101, text: "Saya" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [105],
        correctAnswer: 105, // Hamu -> Kalian
        options: [
            { id: 105, text: "Kalian" },
            { id: 102, text: "Kamu" },
            { id: 103, text: "Dia" },
            { id: 104, text: "Kami" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [103],
        correctAnswer: 103, // Ia -> Dia
        options: [
            { id: 103, text: "Dia" },
            { id: 101, text: "Saya" },
            { id: 102, text: "Kamu" },
            { id: 105, text: "Kalian" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [101],
        correctAnswer: 101, // Au -> Saya
        options: [
            { id: 101, text: "Saya" },
            { id: 102, text: "Kamu" },
            { id: 103, text: "Dia" },
            { id: 104, text: "Kami" }
        ]
    }
];



// Word matching pairs
export const unit1Lesson4WordMatchingPairs: MatchingPair[] = [
    { indonesian: "Saya", batak: "ᯀᯮ" },
    { indonesian: "Kamu", batak: "ᯂᯬ" },
    { indonesian: "Dia", batak: "ᯤᯀ" },
    { indonesian: "Kami", batak: "ᯂᯫᯤ" },
    { indonesian: "Kalian", batak: "ᯂᯔᯮ" }
];

// Matching words configuration
export const unit1Lesson4MatchingWordsConfig = {
    type: "matching-words",
    pairs: unit1Lesson4WordMatchingPairs,
};

// Question types for lesson 4 (only those with existing components)
export const unit1Lesson4QuestionTypes = [
    "vocab-intro",
    "fill-in-the-blank",
    "compose-sentences",
    "word-translation",
    "matching-words"
];
